/**
 * Die Termine der Startseite als Agenda: je Termin ein flacher Chip mit dem Abstand zum
 * Fälligkeitsdatum und daneben zwei Zeilen – oben die Bezeichnung als Überschrift, darunter in
 * festen Spalten der Pfad zum Bezug (Projekt › Antrag › Nachweis), Verantwortlicher und ganz
 * rechts das Datum. Ohne Icons an Name und Datum: Ihre Spalte sagt schon, was sie sind. Gibt es
 * eine Notiz, steht ihr Icon hinter dem Pfad; ein Klick darauf klappt den Termin auf und zeigt die
 * Notiz darunter. Die Spalten haben in jeder Zeile
 * dieselbe Breite, damit Pfade, Daten und Namen untereinander fluchten.
 *
 * Chronologisch und ohne Gruppen: Was überfällig ist, steht von selbst oben, die Dringlichkeit
 * zeigt der Chip – in den Farben der Dringlichkeits-Icons der Termin-Tabellen und der
 * Projektübersicht (`URGENCY_COLOR`).
 *
 * Datum und Verantwortlicher lassen sich direkt in der Zeile ändern, wie in der Nachweisliste:
 * ein Klick öffnet den Kalender bzw. die Auswahl (`InlineDetail`). Ein Klick auf den Rest der Zeile öffnet den
 * Termin. Was geändert werden darf und wie gespeichert wird, entscheidet der Aufrufer.
 */
import React, { useEffect, useState } from "react";
import { Box, Collapse, InputBase, Tooltip, Typography } from "@mui/material";
import {
  StickyNote2Outlined as NoteIcon,
  PriorityHigh as ProblemIcon,
} from "@mui/icons-material";
import Button from "../../../common/inputs/Button";
import { motion } from "framer-motion";
import { Urgency, dueDistance } from "../../../../utils/urgency";
import { colors } from "../../../../styles/colors";
import AbgeschnittenerInhalt from "../../../common/display/AbgeschnittenerInhalt";
import {
  URGENCY_COLOR,
  URGENCY_LABEL,
} from "../../../common/display/DeadlineCell";
import DetailCell from "../../../common/display/DetailCell";
import { InlineUserOption } from "../../../common/inputs/InlineUserPicker";
import { EditableDate, EditableUser } from "./InlineDetail";

export interface AgendaItem {
  id: string;
  title: string;
  /** ISO-Datum; ohne Datum steht ein Strich im Chip */
  dueDate?: string;
  urgency: Urgency;
  /** Pfad zum Bezug (Projekt › Antrag › Nachweis) in der ersten Spalte; leer beim persönlichen */
  breadcrumb: string[];
  responsibleName?: string;
  /** IAM-ID des Verantwortlichen – markiert ihn in der Auswahl */
  responsibleId?: string;
  note?: string | null;
  /** Kennzeichen Problem – rotes Ausrufezeichen statt Notiz-Icon */
  problem: boolean;
  /** Persönlicher Termin ohne Projekt */
  personal: boolean;
  canEditDueDate: boolean;
  canEditResponsible: boolean;
  canEditNote: boolean;
}

export type AgendaUser = InlineUserOption;

interface DeadlineAgendaProps {
  items: AgendaItem[];
  /** Zur Auswahl beim Verantwortlichen; leer, wenn die Liste nicht geladen werden konnte */
  users: AgendaUser[];
  /** Die Notiz gespeichert – `null`, wenn sie geleert wurde */
  onNoteChange: (item: AgendaItem, note: string | null) => void;
  /** Problem gekennzeichnet oder aufgehoben – beschrieben ist es in der Notiz */
  onProblemChange: (item: AgendaItem, problem: boolean) => void;
  onItemClick: (item: AgendaItem) => void;
  onDueDateChange: (item: AgendaItem, dueDate: string) => void;
  onResponsibleChange: (item: AgendaItem, userId: string) => void;
}

/**
 * Pfad | Verantwortlich | Datum – in jeder Zeile gleich, damit sie über alle Zeilen fluchten. Das
 * Datum steht ganz rechts in fester Breite, es ist immer gleich lang; der Name davor hat eine
 * feste Spalte und kürzt mit Ellipse, so drückt ein langer Name nichts zur Seite.
 */
const DETAIL_COLUMNS = "minmax(0, 1fr) minmax(0, 8.5rem) 4.75rem";

/**
 * Höchstbreite jeder Ebene des Pfads außer der letzten, samt Trenner – die Ebenen stehen direkt
 * hintereinander, die letzte bekommt den Rest. So bleibt vom Nachweis etwas zu sehen, wie lang
 * Projekt und Antrag auch heißen. In Einheiten der Spalte (`cqw`), nicht in Prozent: Der Pfad ist
 * nur so breit wie sein Inhalt, damit das Notiz-Icon direkt dahinter steht.
 */
const PATH_LEVEL_MAX_WIDTH = "32cqw";

const CHIP_WIDTH = 44;
const CHIP_HEIGHT = 40;

/** Getönte Fläche des Chips je Dringlichkeit; die Schrift trägt die kräftige Farbe */
const CHIP_BACKGROUND: Record<Urgency, string> = {
  overdue: colors.status.error.light,
  soon: colors.status.warning.light,
  normal: colors.brand.primaryLighter,
};

/**
 * Tooltip des Chips: nur die Dringlichkeit. Datum und Abstand stehen schon in der Zeile bzw. im
 * Chip, die Kategorie zeigte bisher nur die Farbe.
 */
const CHIP_TOOLTIP: Record<Urgency, string> = {
  ...URGENCY_LABEL,
  normal: "Fällig in mehr als 14 Tagen",
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 4 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring" as const, stiffness: 300, damping: 25 },
  },
};

/**
 * Flacher Chip mit dem Abstand zum Fälligkeitsdatum: die Zahl, darunter Tage bzw. Wochen. Fläche
 * und Schrift tragen die Dringlichkeit – rot überfällig, orange in den nächsten 14 Tagen, sonst
 * türkis. Der Tooltip nennt die Dringlichkeit in Worten (`CHIP_TOOLTIP`).
 */
const DistanceChip: React.FC<{ dueDate?: string; urgency: Urgency }> = ({
  dueDate,
  urgency,
}) => {
  const distance = dueDate ? dueDistance(dueDate) : null;
  const color =
    urgency === "normal" ? colors.brand.primary : URGENCY_COLOR[urgency];
  // Ohne „in“ davor liest sich „Tage“ statt „Tagen“
  const unit = distance?.unit === "Tagen" ? "Tage" : distance?.unit;
  const tooltip = dueDate ? CHIP_TOOLTIP[urgency] : "Ohne Datum";

  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          width: CHIP_WIDTH,
          height: CHIP_HEIGHT,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          backgroundColor: CHIP_BACKGROUND[urgency],
          color,
        }}
      >
        <Typography
          sx={{
            fontSize: unit ? "0.95rem" : "0.7rem",
            fontWeight: 700,
            lineHeight: 1.1,
            color: "inherit",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {distance?.value ?? "–"}
        </Typography>
        {unit && (
          <Typography
            sx={{ fontSize: "0.6rem", lineHeight: 1.2, color: "inherit" }}
          >
            {unit}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const detailTextSx = {
  display: "block",
  color: colors.text.secondary,
} as const;

/** Die Notiz in der Schrift der Angaben, mit ihren Zeilenumbrüchen */
const noteTextSx = {
  fontSize: "0.75rem",
  lineHeight: 1.5,
  color: colors.text.secondary,
} as const;

/**
 * Der Pfad zum Bezug in einer Zeile, die Ebenen direkt hintereinander. Jede außer der letzten
 * hat eine Höchstbreite und kürzt dort mit Ellipse; die letzte nimmt sich, was bleibt. Ein Projekttermin hat nur eine Ebene und
 * damit die ganze Spalte. Den ganzen Pfad zeigt der Tooltip.
 */
const ReferencePath: React.FC<{ levels: string[] }> = ({ levels }) => (
  <Tooltip title={levels.length > 1 ? levels.join(" › ") : ""}>
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      {levels.map((level, index) => {
        const last = index === levels.length - 1;
        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              flex: last ? "0 1 auto" : "0 0 auto",
              maxWidth: last ? undefined : PATH_LEVEL_MAX_WIDTH,
            }}
          >
            <Typography
              variant="caption"
              noWrap
              sx={{ ...detailTextSx, minWidth: 0 }}
            >
              {level}
            </Typography>
            {!last && (
              <Typography
                variant="caption"
                sx={{ color: colors.text.disabled, flexShrink: 0, mx: 0.5 }}
              >
                ›
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  </Tooltip>
);

/**
 * Die Notiz des Termins zum Lesen und – mit Recht – zum Ändern. Speichert beim Verlassen des
 * Feldes wie die Notiz am Nachweis (ADR 004).
 *
 * Bewusst kein umrandetes Formularfeld: Die Notiz gehört zum Termin und steht in der Schrift
 * seiner Angaben. Dass sie sich ändern lässt, zeigt die Fläche beim Überfahren.
 */
const NoteEditor: React.FC<{
  item: AgendaItem;
  onSave: (item: AgendaItem, note: string | null) => void;
  onProblemChange: (item: AgendaItem, problem: boolean) => void;
}> = ({ item, onSave, onProblemChange }) => {
  const [draft, setDraft] = useState(item.note ?? "");
  useEffect(() => setDraft(item.note ?? ""), [item.note]);
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: item.problem ? colors.status.error.main : colors.text.disabled,
          mb: 0.25,
        }}
      >
        {item.problem ? "Problem" : "Notiz"}
      </Typography>
      <InputBase
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          const note = draft.trim() ? draft : null;
          if (note !== (item.note ?? null)) onSave(item, note);
        }}
        readOnly={!item.canEditNote}
        multiline
        maxRows={10}
        fullWidth
        inputProps={{ "aria-label": "Notiz" }}
        sx={{
          ...noteTextSx,
          p: 0.75,
          mx: -0.75,
          borderRadius: 1,
          "&:hover": item.canEditNote
            ? { backgroundColor: colors.background.tableHeader }
            : undefined,
          "&.Mui-focused": {
            backgroundColor: colors.background.tableHeader,
          },
        }}
      />
      {/* Die Notiz beschreibt, was hakt – deshalb steht das Kennzeichen hier bei ihr. */}
      {item.canEditNote && (
        <Box sx={{ mt: 1 }}>
          <Button
            variant="secondary"
            size="small"
            onClick={() => onProblemChange(item, !item.problem)}
          >
            {item.problem ? "Problem aufheben" : "Als Problem kennzeichnen"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const DeadlineAgenda: React.FC<DeadlineAgendaProps> = ({
  items,
  users,
  onItemClick,
  onDueDateChange,
  onResponsibleChange,
  onNoteChange,
  onProblemChange,
}) => {
  /** Termine mit aufgeklappter Notiz */
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  if (items.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ padding: "24px 16px", textAlign: "center" }}
      >
        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
          Keine offenen Termine
        </Typography>
      </motion.div>
    );
  }

  const sorted = [...items].sort((a, b) =>
    (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999"),
  );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      {sorted.map((item, index) => {
        const expanded = expandedIds.has(item.id);

        return (
          <motion.div
            key={item.id}
            variants={itemVariants}
            style={{
              borderBottom:
                index < sorted.length - 1
                  ? `1px solid ${colors.border.divider}`
                  : undefined,
            }}
          >
            <Box
              onClick={() => onItemClick(item)}
              sx={{
                py: 1.25,
                // Bis an den Rand der Kachel wie die Zeilen einer Tabelle: Die Kachel hat kein
                // Innenpadding, die Zeile trägt es selbst.
                px: 3,
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                "&:hover": { backgroundColor: colors.state.hover },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
                <DistanceChip dueDate={item.dueDate} urgency={item.urgency} />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AbgeschnittenerInhalt>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        display: "block",
                        fontWeight: 600,
                        color: colors.text.primary,
                        lineHeight: 1.5,
                      }}
                    >
                      {item.title}
                    </Typography>
                  </AbgeschnittenerInhalt>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: DETAIL_COLUMNS,
                      columnGap: 1.5,
                      alignItems: "center",
                      mt: 0.25,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        minWidth: 0,
                        // Bezug für die feste Breite der Ebenen im Pfad (`cqw`)
                        containerType: "inline-size",
                      }}
                    >
                      {item.personal ? (
                        <DetailCell>Persönlich</DetailCell>
                      ) : (
                        <ReferencePath levels={item.breadcrumb} />
                      )}
                      {item.note && (
                        <Tooltip
                          title={
                            expanded
                              ? "Notiz zuklappen"
                              : item.problem
                                ? "Problem zeigen"
                                : "Notiz zeigen"
                          }
                        >
                          <Box
                            component="span"
                            role="button"
                            aria-expanded={expanded}
                            aria-label={item.problem ? "Problem" : "Notiz"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => {
                              event.stopPropagation();
                              toggleExpanded(item.id);
                            }}
                            sx={{
                              display: "flex",
                              flexShrink: 0,
                              p: 0.25,
                              borderRadius: 1,
                              cursor: "pointer",
                              color: item.problem
                                ? colors.status.error.main
                                : expanded
                                  ? colors.brand.primary
                                  : colors.text.secondary,
                              "&:hover": {
                                backgroundColor: colors.background.tableHeader,
                              },
                            }}
                          >
                            {item.problem ? (
                              <ProblemIcon sx={{ fontSize: 15 }} />
                            ) : (
                              <NoteIcon sx={{ fontSize: 15 }} />
                            )}
                          </Box>
                        </Tooltip>
                      )}
                    </Box>

                    <EditableUser
                      userId={item.responsibleId}
                      name={item.responsibleName}
                      users={users}
                      editable={item.canEditResponsible}
                      hideIcon
                      ariaLabel={`Verantwortlichen ändern: ${item.responsibleName || "keiner"}`}
                      onChange={(userId) => onResponsibleChange(item, userId)}
                    />

                    {/* Rechtsbündig, damit das Datum mit dem Rand der Kachel abschließt */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <EditableDate
                        value={item.dueDate}
                        editable={item.canEditDueDate}
                        hideIcon
                        ariaLabel={`Datum ändern: ${item.title}`}
                        onChange={(iso) => onDueDateChange(item, iso)}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {item.note && (
                <Collapse in={expanded} timeout={200} unmountOnExit>
                  {/* Unter den Angaben, bündig mit der Überschrift. Ein Klick ins Feld öffnet
                      nicht den Termin. */}
                  <Box
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      pl: `calc(${CHIP_WIDTH}px + 14px)`,
                      pt: 1,
                      cursor: "auto",
                    }}
                  >
                    <NoteEditor
                      item={item}
                      onSave={onNoteChange}
                      onProblemChange={onProblemChange}
                    />
                  </Box>
                </Collapse>
              )}
            </Box>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default DeadlineAgenda;
